import { useState, useCallback } from 'react';
import { useNotifications } from '../../';
import { getUncachedImageUrl } from '../images';
import { useAvatarCache } from '../contexts';

interface UseAvatarUpdateOptions {
  /**
   * Функция для обновления данных сущности
   */
  refetchData: () => void;
  
  /**
   * Название сущности для уведомления (например, "Аватар фонда", "Изображение пользователя")
   */
  entityName?: string;
  
  /**
   * URL аватара
   */
  avatarUrl?: string;
}

/**
 * Универсальный хук для обработки обновления аватаров
 */
export const useAvatarUpdate = (options: UseAvatarUpdateOptions) => {
  const { refetchData, entityName = 'Аватар', avatarUrl = '' } = options;
  const { createNotification } = useNotifications();
  const { invalidateAvatarsCache, avatarCacheVersion } = useAvatarCache();
  
  // Локальная версия для компонента (используется, если глобальный контекст недоступен)
  const [localAvatarVersion, setLocalAvatarVersion] = useState<number>(Date.now());
  
  /**
   * Обработчик успешной загрузки аватара
   */
  const handleAvatarUploadSuccess = useCallback(() => {
    // Обновляем данные сущности
    refetchData();
    
    // Обновляем версию аватара в глобальном контексте
    invalidateAvatarsCache();
    
    // Также обновляем локальную версию для совместимости
    setLocalAvatarVersion(Date.now());
    
    // Уведомляем пользователя об успешной загрузке
    createNotification({
      name: 'avatar-upload-success',
      title: `${entityName} успешно обновлен`,
      theme: 'success',
    });
  }, [refetchData, createNotification, entityName, invalidateAvatarsCache]);
  
  /**
   * URL аватара с версионированием для предотвращения кеширования браузером
   * Используем глобальную версию кеша, а если она недоступна - локальную
   */
  const versionedAvatarUrl = getUncachedImageUrl(avatarUrl, avatarCacheVersion || localAvatarVersion);
  
  return {
    handleAvatarUploadSuccess,
    versionedAvatarUrl,
    invalidateAvatarsCache,
    avatarVersion: avatarCacheVersion || localAvatarVersion,
    setAvatarVersion: setLocalAvatarVersion
  };
}; 